import { motion, AnimatePresence } from "framer-motion";
import { useGameStore } from "../store";
import { useState, useEffect, useRef } from "react";
import { TimeUpMessage } from "./TimeUpMessage";
import { Timer, XCircle, Trophy, Sparkles, CheckCircle } from "lucide-react";
import { useWebHaptics } from "web-haptics/react";
import { PODIUM_REVEAL_MS } from "./three/constants";

const DEFAULT_TIME = 20; // secondes (fallback si le set ne précise rien)
const WRONG_ANSWER_PENALTY = -500;
// Scoring basé sur le temps de réponse en ms (plus rapide = plus de points)
const MAX_POINTS = 1000; // réponse instantanée
const MIN_POINTS = 100;  // réponse à la dernière seconde
const WRONG_ANSWER_DISPLAY_TIME = 800;
const NEXT_QUESTION_DELAY = 80;

const OPTION_COLORS = [
  { bg: "from-sakura-400 to-sakura-500", border: "border-sakura-300", hover: "hover:shadow-kawaii-lg" },
  { bg: "from-lavender-400 to-lavender-500", border: "border-lavender-300", hover: "hover:shadow-kawaii-lg" },
  { bg: "from-sky-300 to-blue-400", border: "border-sky-200", hover: "hover:shadow-kawaii-lg" },
  { bg: "from-amber-400 to-orange-400", border: "border-amber-300", hover: "hover:shadow-kawaii-lg" },
];

export function Quiz() {
  const {
    currentQuestion,
    timeRemaining,
    movePlayer,
    setCurrentQuestion,
    setTimeRemaining,
    updateScore,
    isMoving,
    questions,
    players,
    finalPlayers,
    socket,
    gameCompleted,
    playerFinished,
    setGameCompleted,
    introPhase,
    timePerQuestion,
  } = useGameStore();

  // Durée effective de la question — venue du serveur, sinon fallback
  const questionTime = timePerQuestion > 0 ? timePerQuestion : DEFAULT_TIME;

  const { trigger } = useWebHaptics();

  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showTimeUp, setShowTimeUp] = useState(false);
  // Le leaderboard de fin est un overlay PLEIN ÉCRAN : affiché tout de suite il
  // masquerait le flash blanc et le podium au moment même où ils apparaissent.
  // On le retient le temps de la révélation, puis il monte par-dessus.
  const [podiumRevealed, setPodiumRevealed] = useState(false);
  const [showWrongAnswer, setShowWrongAnswer] = useState(false);
  const [showCorrectFlash, setShowCorrectFlash] = useState(false);
  const [correctAnswer, setCorrectAnswer] = useState<string>("");
  const [wrongQuestion, setWrongQuestion] = useState<string>("");
  const [transitioning, setTransitioning] = useState(false);
  const questionStartTime = useRef(Date.now());

  // Reset le chrono à chaque nouvelle question
  useEffect(() => {
    questionStartTime.current = Date.now();
  }, [currentQuestion]);

  const isValidQuestion = currentQuestion >= 0 && currentQuestion < questions.length;
  const currentQuestionData = isValidQuestion ? questions[currentQuestion] : null;
  const timeProgress = (timeRemaining / questionTime) * 100;
  // Même gel que le podium : une fois la course finie, le leaderboard affiche le
  // classement de l'arrivée, pas la room qui se vide au fur et à mesure que les
  // joueurs ferment leur téléphone.
  const sortedPlayers = [...(finalPlayers ?? players)].sort((a, b) => b.score - a.score);

  // Timer — bloqué pendant l'intro READY/SET/GO
  useEffect(() => {
    // playerFinished est AUSSI bloquant, pas seulement gameCompleted : un joueur
    // qui a fini attend les autres, et pendant ce temps gameCompleted reste false
    // (le serveur ne le passe à true que quand TOUT le monde a fini). Sans cette
    // garde le chrono se réarmait derrière l'écran d'attente, tombait à 0, et
    // handleTimeUp reculait l'avatar de 2 steps toutes les ~1.9 s jusqu'au départ.
    if (!isValidQuestion || transitioning || gameCompleted || playerFinished || introPhase)
      return;
    const timer = setInterval(() => {
      const t = useGameStore.getState().timeRemaining;
      if (t > 0) {
        setTimeRemaining(t - 1);
      } else {
        clearInterval(timer);
        handleTimeUp();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [currentQuestion, transitioning, gameCompleted, playerFinished, introPhase]);

  // Haptic tick quand le temps est bas (< 5s)
  useEffect(() => {
    if (timeRemaining <= 5 && timeRemaining > 0 && !transitioning) {
      trigger("nudge");
    }
  }, [timeRemaining]);

  const handleTimeUp = async () => {
    // Relu dans le store plutôt que capturé : handleTimeUp vit dans la closure de
    // l'intervalle, sa copie de playerFinished peut dater du render précédent.
    if (gameCompleted || useGameStore.getState().playerFinished) return;
    trigger([100, 80, 100]);
    setTransitioning(true);
    setShowTimeUp(true);
    movePlayer(-2);
    await new Promise((r) => setTimeout(r, WRONG_ANSWER_DISPLAY_TIME));
    setShowTimeUp(false);
    await new Promise((r) => setTimeout(r, NEXT_QUESTION_DELAY));
    if (currentQuestion >= questions.length - 1) {
      setGameCompleted(true);
    } else {
      setCurrentQuestion(currentQuestion + 1);
      setTimeRemaining(questionTime);
    }
    setTransitioning(false);
  };

  const handleAnswer = async (answerIndex: number) => {
    if (gameCompleted || !currentQuestionData || isMoving || transitioning) return;

    setTransitioning(true);
    setSelectedAnswer(answerIndex);
    const isCorrect = answerIndex === currentQuestionData.correctAnswer;

    // Temps de réponse en ms → ratio 1.0 (instantané) à 0.0 (timeout)
    const elapsedMs = Date.now() - questionStartTime.current;
    const timeRatio = Math.max(0, 1 - elapsedMs / (questionTime * 1000));

    // Score : interpolation quadratique entre MIN_POINTS et MAX_POINTS
    const points = isCorrect
      ? Math.round(MIN_POINTS + (MAX_POINTS - MIN_POINTS) * timeRatio * timeRatio)
      : WRONG_ANSWER_PENALTY;

    // Mouvement proportionnel : courbe quadratique, 1-10 steps
    const moveSteps = isCorrect
      ? Math.max(1, Math.round(1 + timeRatio * timeRatio * 9))
      : -1;

    // Haptic feedback
    if (isCorrect) {
      trigger("success");
    } else {
      trigger([100, 80, 100]);
    }

    updateScore(points);
    movePlayer(moveSteps);

    if (!isCorrect) {
      setCorrectAnswer(currentQuestionData.options[currentQuestionData.correctAnswer]);
      setWrongQuestion(currentQuestionData.question);
      setShowWrongAnswer(true);
      await new Promise((r) => setTimeout(r, WRONG_ANSWER_DISPLAY_TIME));
      setShowWrongAnswer(false);
      await new Promise((r) => setTimeout(r, NEXT_QUESTION_DELAY));
    } else {
      setShowCorrectFlash(true);
      await new Promise((r) => setTimeout(r, 300));
      setShowCorrectFlash(false);
      await new Promise((r) => setTimeout(r, NEXT_QUESTION_DELAY));
    }

    if (currentQuestion >= questions.length - 1) {
      setGameCompleted(true);
    } else {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setTimeRemaining(questionTime);
    }
    setTransitioning(false);
  };

  // Fenêtre de révélation du podium avant que le leaderboard ne recouvre la scène.
  useEffect(() => {
    if (!gameCompleted) {
      setPodiumRevealed(false);
      return;
    }
    const t = setTimeout(() => setPodiumRevealed(true), PODIUM_REVEAL_MS);
    return () => clearTimeout(t);
  }, [gameCompleted]);

  const getMedalEmoji = (index: number) => {
    switch (index) {
      case 0: return "bg-gradient-to-r from-yellow-300 to-amber-400";
      case 1: return "bg-gradient-to-r from-gray-300 to-gray-400";
      case 2: return "bg-gradient-to-r from-amber-500 to-orange-500";
      default: return "bg-gradient-to-r from-lavender-300 to-lavender-400";
    }
  };

  // --- Intro Ready / Set / Go (uniquement côté joueur, 6s avant la 1re question) ---
  // Petit label discret en bas, ne masque pas l'avatar pendant l'orbite caméra.
  if (introPhase) {
    const phaseLabel = { ready: "Ready", set: "Set", go: "Go" }[introPhase];
    const phaseBorder = {
      ready: "border-sakura-200/70",
      set: "border-lavender-200/70",
      go: "border-emerald-200/70",
    }[introPhase];
    const phaseText = {
      ready: "text-sakura-500",
      set: "text-lavender-500",
      go: "text-emerald-500",
    }[introPhase];
    return (
      <div className="fixed inset-x-0 bottom-0 z-30 flex justify-center items-end pb-6 font-kawaii pointer-events-none">
        <AnimatePresence mode="wait">
          <motion.div
            key={introPhase}
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 1.02 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className={`px-5 py-2 rounded-full bg-white/85 backdrop-blur-md shadow-kawaii border-2 ${phaseBorder}`}
          >
            <span className={`text-sm font-black tracking-[0.3em] uppercase ${phaseText}`}>
              {phaseLabel}
            </span>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  // --- Waiting for others screen (joueur a fini mais pas tous) ---
  if (playerFinished && !gameCompleted) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-x-0 bottom-0 z-40 flex justify-center items-end p-3 pb-4 font-kawaii"
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", bounce: 0.4 }}
          className="w-full max-w-lg p-6 bg-white/90 backdrop-blur-xl rounded-blob shadow-kawaii-lg border-2 border-lavender-200/50 text-center"
        >
          <motion.div
            animate={{ rotate: [0, -10, 10, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="inline-block mb-3"
          >
            <Sparkles className="w-12 h-12 text-star" />
          </motion.div>
          <h2 className="text-2xl font-black text-lavender-500 mb-2">
            Bien joué !
          </h2>
          <p className="text-gray-500 font-bold mb-3">
            En attente des autres joueurs...
          </p>
          <div className="flex items-center justify-center gap-2">
            <span className="font-black text-sakura-500 text-xl">
              {sortedPlayers.find(p => p.id === socket?.id)?.score.toLocaleString() || 0}
            </span>
            <span className="text-gray-400 font-bold">pts</span>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  // Fenêtre de révélation : on ne rend RIEN pour laisser la scène 3D (flash blanc
  // puis podium) occuper tout l'écran du joueur, comme sur le grand écran.
  if (gameCompleted && !podiumRevealed) return null;

  // --- Game Complete screen ---
  if (gameCompleted) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 backdrop-blur-md p-4 font-kawaii"
      >
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", bounce: 0.5 }}
          className="bg-white/90 backdrop-blur-xl rounded-blob shadow-kawaii-lg border-2 border-star/30 p-8 w-full max-w-lg"
        >
          <div className="text-center mb-6">
            <motion.div
              animate={{ rotate: [0, -10, 10, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <Trophy className="w-16 h-16 text-star mx-auto mb-3" />
            </motion.div>
            <h2 className="text-3xl font-black text-sakura-500 mb-1">
              Game Complete!
            </h2>
            <p className="text-lavender-400 font-bold">Final Rankings</p>
          </div>

          <div className="space-y-3 mb-6">
            {sortedPlayers.map((player, index) => (
              <motion.div
                key={player.id}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ type: "spring", bounce: 0.4, delay: index * 0.15 }}
                className={`rounded-xl p-4 flex items-center justify-between shadow-kawaii-card ${
                  player.id === socket?.id
                    ? "bg-gradient-to-r from-sakura-50 to-lavender-50 border-2 border-sakura-300"
                    : "bg-white border-2 border-gray-100"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-black text-sm ${getMedalEmoji(index)}`}>
                    {index + 1}
                  </div>
                  <span className="font-bold text-gray-700">
                    {player.name}
                    {player.id === socket?.id && (
                      <span className="ml-1.5 text-sakura-400 text-sm">(You)</span>
                    )}
                  </span>
                </div>
                <span className="font-black text-lavender-500">
                  {player.score.toLocaleString()}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    );
  }

  // --- Quiz UI ---
  return (
    <>
      <TimeUpMessage show={showTimeUp} />

      {/* Correct answer flash */}
      <AnimatePresence>
        {showCorrectFlash && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.5 }}
            className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none"
          >
            <div className="bg-mint/30 backdrop-blur-sm rounded-full p-10">
              <CheckCircle className="w-24 h-24 text-emerald-500" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Wrong answer popup */}
      <AnimatePresence>
        {showWrongAnswer && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="fixed inset-0 flex items-center justify-center z-50 bg-black/40 backdrop-blur-sm font-kawaii"
          >
            <motion.div
              initial={{ y: -20, rotate: -2 }}
              animate={{ y: 0, rotate: 0 }}
              className="bg-white/95 backdrop-blur-xl p-7 rounded-blob shadow-kawaii-lg border-2 border-red-200 text-center max-w-sm mx-4"
            >
              <motion.div
                animate={{ rotate: [0, -10, 10, -5, 5, 0] }}
                transition={{ duration: 0.5 }}
              >
                <XCircle className="w-14 h-14 text-red-400 mx-auto mb-3" />
              </motion.div>
              <h3 className="text-2xl font-black text-red-500 mb-3">
                Wrong!
              </h3>
              <div className="bg-red-50 p-3 rounded-kawaii mb-4 border border-red-100">
                <p className="text-sm text-gray-600">{wrongQuestion}</p>
              </div>
              <p className="text-sm text-gray-400 mb-1">Correct answer:</p>
              <p className="text-lg font-black text-sakura-500 mb-3">
                {correctAnswer}
              </p>
              <p className="text-red-400 font-bold text-sm">
                -{Math.abs(WRONG_ANSWER_PENALTY)} pts
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quiz panel */}
      {!transitioning && !isMoving && currentQuestionData && !gameCompleted && (
        <div className="fixed inset-x-0 bottom-0 z-40 flex justify-center items-end p-3 pb-4 font-kawaii">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: "spring", bounce: 0.3 }}
            className="w-full max-w-lg p-5 bg-white/65 backdrop-blur-xl rounded-blob shadow-kawaii-lg border-2 border-sakura-200/50"
          >
            {/* Timer */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Timer className={`w-5 h-5 ${timeRemaining <= 5 ? "text-red-400 animate-wiggle" : "text-lavender-400"}`} />
                  <span className={`text-lg font-black ${timeRemaining <= 5 ? "text-red-400" : "text-lavender-500"}`}>
                    {timeRemaining}s
                  </span>
                </div>
                <span className="text-sm font-bold text-gray-400">
                  {currentQuestion + 1}/{questions.length}
                </span>
              </div>
              <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  className={`h-full rounded-full ${
                    timeRemaining <= 5
                      ? "bg-gradient-to-r from-red-400 to-orange-400"
                      : "bg-gradient-to-r from-sakura-400 to-lavender-400"
                  }`}
                  animate={{ width: `${timeProgress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>

            {/* Question */}
            <motion.p
              key={currentQuestion}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-lg font-bold text-gray-700 mb-4 leading-snug"
            >
              {currentQuestionData.question}
            </motion.p>

            {/* Options */}
            <div className="grid grid-cols-1 gap-2.5">
              {currentQuestionData.options.map((option, index) => {
                const color = OPTION_COLORS[index % OPTION_COLORS.length];
                const isSelected = selectedAnswer === index;
                return (
                  <motion.button
                    key={`${currentQuestion}-${index}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.06, type: "spring", bounce: 0.4 }}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className={`w-full p-4 text-left text-base font-bold rounded-xl transition-all shadow-kawaii-card border-2 ${
                      isSelected
                        ? `bg-gradient-to-r ${color.bg} text-white border-transparent shadow-kawaii-lg`
                        : `bg-white text-gray-700 ${color.border} ${color.hover}`
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    onClick={() => {
                      trigger("nudge");
                      handleAnswer(index);
                    }}
                    disabled={selectedAnswer !== null || transitioning}
                  >
                    <span className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-black shrink-0 ${
                        isSelected
                          ? "bg-white/30 text-white"
                          : `bg-gradient-to-br ${color.bg} text-white`
                      }`}>
                        {String.fromCharCode(65 + index)}
                      </span>
                      {option}
                    </span>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}
